import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countEmptyCells, readTables } from "@/office/word/tables";

export const lektionTabelleAnlegen: Lesson = {
  id: "tabelle-anlegen",
  title: "Eine Tabelle anlegen",
  summary: "Zeilen und Spalten einfügen – und wann eine Tabelle überhaupt die richtige Wahl ist.",
  durationMinutes: 8,
  learningGoals: [
    "Tabellen in der passenden Größe einfügen",
    "Zeilen und Spalten nachträglich ergänzen",
    "Tabellen von Tabulatoren unterscheiden",
  ],
  steps: [
    {
      id: "einfuegen",
      title: "Die erste Tabelle",
      explanation: [
        "Eine Tabelle ordnet Informationen in Zeilen und Spalten. Immer dann, wenn du " +
          "gleichartige Angaben mehrfach untereinander schreibst – Name, Datum, Betrag – " +
          "gehört das in eine Tabelle und nicht in Fließtext mit Tabulatoren.",
        "Der Unterschied ist derselbe wie bei den Listen im Absatz-Modul: Mit Tabulatoren " +
          "ausgerichtete Spalten verrutschen, sobald ein Eintrag länger wird. Eine echte " +
          "Tabelle hält die Struktur, egal wie viel Text in einer Zelle steht.",
        "Zum Einfügen: „Einfügen“ → „Tabelle“, dann im Raster die gewünschte Größe " +
          "aufziehen. Mit der Tab-Taste springst du von Zelle zu Zelle – und am Ende der " +
          "letzten Zeile erzeugt Tab automatisch eine neue Zeile.",
      ],
      keyPoints: [
        "Einfügen → Tabelle → Raster aufziehen.",
        "Tab springt zur nächsten Zelle, am Tabellenende erzeugt es eine neue Zeile.",
        "Gleichartige Daten gehören in eine Tabelle, nicht in Tabulatorspalten.",
      ],
      task: {
        instruction:
          "Füge eine Tabelle mit mindestens 3 Spalten und mindestens 4 Zeilen in dein " +
          "Dokument ein und fülle sie vollständig aus.",
        hint: "Zum Beispiel eine Teilnehmerliste mit Name, Abteilung und E-Mail.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Tabelle.",
              "Einfügen → Tabelle, dann im Raster die Größe aufziehen.",
            );
          }

          const tabelle = tabellen[0];
          const leer = countEmptyCells(tabelle);

          return fromCriteria(
            [
              criterion(`Mindestens 3 Spalten (gefunden: ${tabelle.columnCount})`, tabelle.columnCount >= 3),
              criterion(`Mindestens 4 Zeilen (gefunden: ${tabelle.rowCount})`, tabelle.rowCount >= 4),
              criterion(`Alle Zellen sind gefüllt (leer: ${leer})`, leer === 0),
            ],
            {
              success: "Die Tabelle steht und ist vollständig ausgefüllt.",
              failure: "An der Tabelle fehlt noch etwas.",
              hint: "Neue Zeilen bekommst du am schnellsten mit Tab in der letzten Zelle.",
            },
          );
        },
      },
    },
    {
      id: "zeilen-und-spalten",
      title: "Zeilen und Spalten ändern",
      explanation: [
        "Tabellen wachsen fast immer. Word bietet dafür mehrere Wege: Über die " +
          "Registerkarte „Layout“ (sie erscheint, sobald der Cursor in einer Tabelle " +
          "steht) findest du „Darüber einfügen“, „Darunter einfügen“, „Links einfügen“ " +
          "und „Rechts einfügen“.",
        "Schneller geht es mit dem kleinen Pluszeichen: Fährst du mit der Maus an den " +
          "linken oder oberen Rand zwischen zwei Zeilen beziehungsweise Spalten, blendet " +
          "Word einen Kreis mit Plus ein. Ein Klick fügt an genau dieser Stelle ein.",
        "Zum Löschen markierst du die Zeile oder Spalte und wählst unter „Layout“ → " +
          "„Löschen“. Wichtig: Die Entf-Taste leert nur den Inhalt, sie entfernt die Zeile " +
          "nicht.",
      ],
      keyPoints: [
        "Die Registerkarte „Layout“ erscheint nur, wenn der Cursor in der Tabelle steht.",
        "Das Pluszeichen am Rand fügt genau an dieser Stelle ein.",
        "Entf leert Zellen, Layout → Löschen entfernt Zeilen und Spalten.",
      ],
      task: {
        instruction: "Erweitere deine Tabelle um eine zusätzliche Zeile – sie soll jetzt mindestens 5 Zeilen haben.",
        hint: "Cursor in die letzte Zelle setzen und Tab drücken.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail("Im Dokument gibt es noch keine Tabelle.", "Lege zuerst eine Tabelle an.");
          }
          const tabelle = tabellen[0];
          if (tabelle.rowCount < 5) {
            return fail(
              `Die Tabelle hat erst ${tabelle.rowCount} Zeilen.`,
              "In der letzten Zelle erzeugt Tab automatisch eine weitere Zeile.",
            );
          }
          return pass(`Die Tabelle hat jetzt ${tabelle.rowCount} Zeilen.`);
        },
      },
    },
  ],
};
